/**
 * BaselineExportTab Component
 *
 * Allows users to download their approved baseline configuration report
 * in CSV or HTML format. Part of the Configuration Drift hub.
 *
 * Extracted from pages/BaselineReports.tsx into a tab component.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import type { Tenant } from '@/types'
import {
  DocumentArrowDownIcon,
  DocumentTextIcon,
  TableCellsIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

export interface BaselineExportTabProps {
  tenantId: string | undefined
}

export default function BaselineExportTab({ tenantId }: BaselineExportTabProps) {
  const [selectedFormat, setSelectedFormat] = useState<'csv' | 'html'>('csv')
  const [isDownloading, setIsDownloading] = useState(false)

  // Use cached tenants query to resolve tenant name for the download filename
  const { data: tenants = [] } = useQuery<Tenant[]>({
    queryKey: ['tenants'],
    queryFn: api.getTenants,
    staleTime: 5 * 60 * 1000,
  })

  const handleDownload = async () => {
    if (!tenantId) {
      toast.error('Please select a tenant first')
      return
    }

    setIsDownloading(true)

    try {
      const blob = await api.downloadBaselineReport(tenantId, selectedFormat)

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      const tenant = tenants.find((t) => t.tenantId === tenantId)
      const tenantName = tenant?.name?.replace(/[^a-zA-Z0-9]/g, '-') || tenantId
      const timestamp = new Date().toISOString().split('T')[0]
      link.download = `baseline-report-${tenantName}-${timestamp}.${selectedFormat}`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success(`${selectedFormat.toUpperCase()} report downloaded successfully!`)
    } catch (err: any) {
      console.error('Download error:', err)
      if (err.response?.status === 404) {
        toast.error('No baseline configuration found for this tenant. Run a configuration scan first to establish a baseline.')
      } else if (err.response?.status === 403) {
        toast.error('You do not have access to this tenant.')
      } else {
        toast.error('Failed to download report. Please try again.')
      }
    } finally {
      setIsDownloading(false)
    }
  }

  // No tenant selected state
  if (!tenantId) {
    return (
      <div className="bg-dark-800 rounded-2xl border border-dark-700 overflow-hidden">
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 px-8 py-12 text-center border-b border-dark-700">
          <div className="w-24 h-24 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-amber-500/30">
            <DocumentArrowDownIcon className="h-12 w-12 text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-100 mb-4">Select a Tenant</h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            Please select a tenant to download their baseline configuration report.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-dark-800 rounded-2xl border border-dark-700 overflow-hidden">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-primary-500/10 to-indigo-500/10 px-8 py-8 border-b border-dark-700">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-primary-500/20 to-indigo-500/20 rounded-full flex items-center justify-center border border-primary-500/30">
            <DocumentArrowDownIcon className="h-8 w-8 text-primary-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-100">Baseline Configuration Report</h2>
            <p className="text-slate-400">
              Export your approved Microsoft 365 baseline configuration
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Section */}
      <div className="px-8 py-8">
        {/* Format Selection */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-slate-300 mb-3">Report Format</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
            {/* CSV Option */}
            <button
              type="button"
              onClick={() => setSelectedFormat('csv')}
              className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                selectedFormat === 'csv'
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-dark-600 bg-dark-700 hover:border-dark-500'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedFormat === 'csv' ? 'bg-emerald-500/20' : 'bg-dark-600'
                  }`}
                >
                  <TableCellsIcon
                    className={`h-5 w-5 ${selectedFormat === 'csv' ? 'text-emerald-400' : 'text-slate-400'}`}
                  />
                </div>
                <div>
                  <div className="font-medium text-slate-100">CSV Export</div>
                  <div className="text-xs text-slate-500">Spreadsheet-friendly format</div>
                </div>
              </div>
              {selectedFormat === 'csv' && (
                <div className="absolute top-2 right-2 w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </button>

            {/* HTML Option */}
            <button
              type="button"
              onClick={() => setSelectedFormat('html')}
              className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                selectedFormat === 'html'
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-dark-600 bg-dark-700 hover:border-dark-500'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedFormat === 'html' ? 'bg-cyan-500/20' : 'bg-dark-600'
                  }`}
                >
                  <DocumentTextIcon
                    className={`h-5 w-5 ${selectedFormat === 'html' ? 'text-cyan-400' : 'text-slate-400'}`}
                  />
                </div>
                <div>
                  <div className="font-medium text-slate-100">HTML Report</div>
                  <div className="text-xs text-slate-500">Formatted for auditors</div>
                </div>
              </div>
              {selectedFormat === 'html' && (
                <div className="absolute top-2 right-2 w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Download Button */}
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
            isDownloading
              ? 'bg-dark-700 text-slate-500 cursor-not-allowed'
              : 'btn-primary'
          }`}
        >
          {isDownloading ? (
            <>
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
              Generating Report...
            </>
          ) : (
            <>
              <ArrowDownTrayIcon className="h-5 w-5" />
              Download {selectedFormat.toUpperCase()} Report
            </>
          )}
        </button>

        {/* Help Text */}
        <p className="mt-4 text-sm text-slate-500">
          This report contains your approved Microsoft 365 baseline configuration. Use it for
          auditor documentation or compliance verification.
        </p>
      </div>
    </div>
  )
}
